import { devLog } from './devLog';
/*
 * Copyright © 2024-present Mia s.r.l.
 * All rights reserved
 */

/**
 * Injects error capturing script into an iframe
 * This allows parent window to receive JavaScript errors from the iframe
 * @param {HTMLIFrameElement} iframe - The iframe element to inject into
 */
export function injectErrorCapture(iframe) {
  if (!iframe || !iframe.contentWindow) {
    return;
  }

  try {
    const iframeWindow = iframe.contentWindow;
    const iframeDocument = iframeWindow.document;

    // Check if script is already injected
    if (iframeWindow.__miaFlowErrorCaptureInstalled) {
      return;
    }

    // Create and inject the error capture script
    const script = iframeDocument.createElement('script');
    script.textContent = `
      (function() {
        // Mark as installed
        window.__miaFlowErrorCaptureInstalled = true;

        // Function to send errors to parent window
        function sendErrorToParent(type, message, stack) {
          try {
            window.parent.postMessage({
              source: 'mia-flow-preview-client', // eslint-disable-line spellcheck/spell-checker
              type: type,
              message: message,
              stack: stack,
              timestamp: new Date().toISOString()
            }, '*');
          } catch (err) {
            // Silently fail if postMessage doesn't work
          }
        }

        // Capture uncaught errors
        window.addEventListener('error', function(event) {
          const message = event.message || 'Unknown error';
          const stack = event.error && event.error.stack 
            ? event.error.stack 
            : (event.filename + ':' + event.lineno + ':' + event.colno);
          
          sendErrorToParent('error', message, stack);
        });

        // Capture unhandled promise rejections
        window.addEventListener('unhandledrejection', function(event) { // eslint-disable-line spellcheck/spell-checker
          const message = event.reason && event.reason.message 
            ? event.reason.message 
            : String(event.reason);
          const stack = event.reason && event.reason.stack 
            ? event.reason.stack 
            : 'Unhandled Promise Rejection: ' + message;
          
          sendErrorToParent('unhandledrejection', message, stack); // eslint-disable-line spellcheck/spell-checker
        });

        // Intercept console.error
        const originalConsoleError = console.error;
        console.error = function(...args) {
          // Call original console.error
          originalConsoleError.apply(console, args);
          
          // Send to parent
          const message = args.map(arg => {
            if (typeof arg === 'object') {
              try {
                return JSON.stringify(arg, null, 2);
              } catch (e) {
                return String(arg);
              }
            }
            return String(arg);
          }).join(' ');
          
          sendErrorToParent('console.error', message, message);
        };

        // Intercept console.warn for completeness
        const originalConsoleWarn = console.warn;
        console.warn = function(...args) {
          // Call original console.warn
          originalConsoleWarn.apply(console, args);
          
          // Send to parent (less critical, but still useful)
          const message = args.map(arg => {
            if (typeof arg === 'object') {
              try {
                return JSON.stringify(arg, null, 2);
              } catch (e) {
                return String(arg);
              }
            }
            return String(arg);
          }).join(' ');
          
          sendErrorToParent('console.warn', '⚠️ ' + message, message);
        };

        // Vite's dev client injects its own <vite-error-overlay> custom element
        // directly into the DOM when a file fails to *transform* (syntax error,
        // unterminated JSX, etc.) — this happens before any app code runs, so
        // window.onerror/unhandledrejection never fire and the hooks above never
        // see it. Watch the DOM for that element instead and read its message out
        // of its shadow root.
        function reportViteOverlay(overlayEl) {
          try {
            const root = overlayEl.shadowRoot || overlayEl;
            const pick = (sel) => {
              const el = root.querySelector(sel);
              return el ? el.textContent.trim() : '';
            };
            const message = pick('.message') || pick('pre.message');
            const file = pick('.file');
            const frame = pick('.frame') || pick('pre.frame');
            const full = [message, file, frame].filter(Boolean).join('\\n\\n') || overlayEl.textContent.trim();
            if (full) {
              sendErrorToParent('vite-overlay', full, full);
            }
          } catch (err) {
            // Best-effort only.
          }
        }

        function scanForViteOverlay(node) {
          if (!node || node.nodeType !== 1) return;
          if (node.tagName === 'VITE-ERROR-OVERLAY') {
            reportViteOverlay(node);
            return;
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('vite-error-overlay').forEach(reportViteOverlay);
          }
        }

        // Catch one already present at inject time (fast refresh can inject
        // before this script runs on a hard reload).
        scanForViteOverlay(document.documentElement);

        if (window.MutationObserver) {
          const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
              mutation.addedNodes.forEach(scanForViteOverlay);
            });
          });
          observer.observe(document.documentElement, { childList: true, subtree: true });
        }

      })();
    `;

    // Inject at the beginning of the document
    if (iframeDocument.head) {
      iframeDocument.head.insertBefore(script, iframeDocument.head.firstChild);
    } else if (iframeDocument.body) {
      iframeDocument.body.insertBefore(script, iframeDocument.body.firstChild);
    } else {
      // Document not ready, wait for DOMContentLoaded
      iframeDocument.addEventListener('DOMContentLoaded', () => {
        if (iframeDocument.head) {
          iframeDocument.head.insertBefore(script, iframeDocument.head.firstChild);
        }
      });
    }

    devLog('📡 Injected error capture script into iframe');
  } catch (error) {
    // This will fail for cross-origin iframes, which is expected
    if (error.name === 'SecurityError' || error.message.includes('cross-origin')) {
      devLog('🔒 Cannot inject error capture into cross-origin iframe (expected)');
    } else {
      console.error('❌ Failed to inject error capture:', error);
    }
  }
}
